// stats-aggregate — wrong-answer stats aggregation core (single source of truth)
//
// DEBT-19BO+-STATS-MIRROR-PARITY-GATE resolution: the per-item aggregation
// step (accumulate caseAgg / catAgg / cogAgg, sort, bucket 14-day trend) was
// duplicated verbatim between:
//   1. packages/web/src/lib/stats.ts `wrongAnswerStats` (renders local UI)
//   2. packages/ingest/src/diagnose/aggregate-stats.ts `aggregateDiagnoseStats`
//      (server-side cron diagnose data source)
// The two consumers have different INPUT shapes (web reads CourseProgress dict;
// ingest LWW-merges ProgressEventRow[] from D1) and different OUTPUT contracts
// (web returns uncapped arrays for bar-chart max; ingest caps to
// DiagnoseRequestSchema max 50/20/10 before sending to LLM). So the top-level
// entry points cannot merge. But the inner aggregation — given already-merged
// entries + lookup maps — was copy-pasted. That core lives here now.
//
// Both consumers keep their input-preprocessing + cap layers; they delegate the
// shared aggregation to `aggregateWrongAnswerStats`. Cap drift is no longer
// possible: cap is ingest's adapter responsibility, web's view contract is
// uncapped by design (wrong-answers-view.tsx computes global max across the
// full byCase array).
//
// Convention: pure function colocated with schemas (precedent: isUnresolved /
// subjectiveNeedsRevisit live in course-progress.ts). No React, no IDB, no D1.
import { isUnresolved } from './course-progress.js';
/** Cognitive-level column order used by every wrong-answer aggregation surface.
 *  Matches COG_ORDER in web/stats.ts and ingest/aggregate-stats.ts (now removed
 *  from both — this is the one copy). 'unlabeled' is the tail bucket for items
 *  missing the cognitiveLevel field. */
const COG_ORDER = ['recall', 'understand', 'apply', 'analyze', 'evaluate'];
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Aggregate already-merged wrong-answer entries into the byCase / byCategory /
 * byCognitive / recentTrend dimensions. Single source of truth — both web
 * `wrongAnswerStats` and ingest `aggregateDiagnoseStats` delegate here.
 *
 * @param entries  itemId → latest WrongAnswerEntry (CALLER merges: web reads
 *                 the CourseProgress.wrongAnswers dict directly; ingest LWW-
 *                 merges ProgressEventRow[] by client_occurred_at). Entries
 *                 whose id is neither an mcq nor a tf are skipped (mirror
 *                 web/stats.ts:299-301 — only graded items count).
 * @param lookups  mcq/tf/case metadata maps (CALLER builds from package data).
 * @param now      reference timestamp for the 14-day trend window (ms epoch).
 *                 Injected for deterministic tests; web passes Date.now(),
 *                 ingest receives it from its caller.
 * @returns        uncapped aggregation (see WrongAnswerStatsAggregated).
 */
export function aggregateWrongAnswerStats(entries, lookups, now) {
    const { mcqById, tfById, caseById } = lookups;
    let total = 0;
    let unresolvedCount = 0;
    // case → counts
    const caseAgg = new Map();
    // category → counts (mcq/tf.category from our enum)
    const catAgg = new Map();
    // cognitive level → counts
    const cogAgg = new Map();
    for (const lv of COG_ORDER)
        cogAgg.set(lv, 0);
    cogAgg.set('unlabeled', 0);
    // Materialize once: the trend pass re-iterates entries, so collect the
    // filtered (mcq/tf-matching) ids to avoid re-checking the lookup maps.
    const matchedEntries = [];
    for (const [id, entry] of entries) {
        // Only count items that exist in pkg (mirror web/stats.ts:300-302).
        if (!mcqById.has(id) && !tfById.has(id))
            continue;
        matchedEntries.push([id, entry]);
        total++;
        const u = isUnresolved(entry);
        if (u)
            unresolvedCount++;
        const cId = entry.caseId ?? '__unknown';
        const cur = caseAgg.get(cId) ?? { count: 0, unresolved: 0 };
        cur.count++;
        if (u)
            cur.unresolved++;
        caseAgg.set(cId, cur);
        // category + cognitiveLevel from underlying question (entry doesn't carry these).
        const q = mcqById.get(id) ?? tfById.get(id);
        const cat = q?.category ?? 'unlabeled';
        const cc = catAgg.get(cat) ?? { count: 0, unresolved: 0 };
        cc.count++;
        if (u)
            cc.unresolved++;
        catAgg.set(cat, cc);
        const cogKey = (q?.cognitiveLevel ?? 'unlabeled');
        cogAgg.set(cogKey, (cogAgg.get(cogKey) ?? 0) + 1);
    }
    const byCase = [...caseAgg.entries()]
        .map(([caseId, v]) => ({
        caseId,
        caseName: caseById.get(caseId)?.title ?? caseId,
        count: v.count,
        unresolved: v.unresolved,
    }))
        .sort((a, b) => b.unresolved - a.unresolved || b.count - a.count);
    const byCategory = [...catAgg.entries()]
        .map(([category, v]) => ({ category, count: v.count, unresolved: v.unresolved }))
        .sort((a, b) => b.count - a.count);
    const byCognitive = [...COG_ORDER, 'unlabeled'].map((lv) => ({
        level: lv,
        count: cogAgg.get(lv) ?? 0,
    }));
    // 14-day trend: count items whose latest lastWrong day-bucket falls in window.
    const trendBuckets = new Array(14).fill(0);
    for (const [, entry] of matchedEntries) {
        const ts = Date.parse(entry.lastWrong);
        if (!Number.isFinite(ts))
            continue;
        const offset = Math.floor((now - ts) / DAY_MS);
        if (offset >= 0 && offset < 14)
            trendBuckets[offset] += 1;
    }
    const recentTrend = trendBuckets.map((wrongEvents, dayOffset) => ({ dayOffset, wrongEvents }));
    return {
        total,
        unresolved: unresolvedCount,
        resolved: total - unresolvedCount,
        byCase,
        byCategory,
        byCognitive,
        recentTrend,
    };
}
//# sourceMappingURL=stats-aggregate.js.map